import { defaultStyle } from '../../layers/singleImage/SingleImageFootprintsLayers';

/**
 * Builds a WebGL flat style expression that colors features by their `classId` property.
 * Falls back to hover-aware default styling when no classId is set.
 *
 * The style uses a priority chain:
 *   1. Hover state (classId-aware highlight)
 *   2. Classification color (based on classId → color mapping)
 *   3. Default unclassified color
 *
 * @param {Object} classColorMap - Map of classId → rgba string, e.g. { 'easy': 'rgba(144,238,144,0.7)' }
 * @returns {Object} WebGL flat style object compatible with WebGLVectorLayer.setStyle()
 */
export function buildClassificationStyle(classColorMap) {
	const classIds = Object.keys(classColorMap);

	if (!classIds.length) {
		return defaultStyle;
	}

	// Build a case expression: for each classId, map to its fill color.
	// Structure: ['case', condition1, output1, condition2, output2, ..., fallback]
	const fillCases = [];
	const strokeCases = [];

	// Hover takes highest priority
	fillCases.push(['>', ['get', 'hovering'], 0], 'rgba(255, 255, 255, 0.4)');
	strokeCases.push(['>', ['get', 'hovering'], 0], 'rgba(255, 255, 0, 0.9)');

	// Classification colors
	for (const classId of classIds) {
		const color = classColorMap[classId];
		fillCases.push(['==', ['get', 'classId'], classId], color);
		strokeCases.push(['==', ['get', 'classId'], classId], 'rgba(0, 0, 0, 0.7)');
	}

	// Fallback (unclassified or no classId set)
	fillCases.push('rgba(100, 100, 100, 0.3)');
	strokeCases.push('rgba(0, 0, 0, 0.5)');

	return {
		'fill-color': ['case', ...fillCases],
		'stroke-color': ['case', ...strokeCases],
		'stroke-width': 2,
	};
}

/**
 * Converts classification results into a classId → rgba color string map.
 *
 * @param {Object} classificationResults - Output from classifyDspFeatures()
 * @param {Array} filterClasses - The filter.classes array with id, color, opacity
 * @returns {Object} Map of classId → rgba string
 */
export function buildClassColorMap(classificationResults, filterClasses) {
	const map = {};

	for (const cls of filterClasses) {
		const result = classificationResults[cls.id];
		if (result?.colorObj) {
			const { r, g, b, a } = result.colorObj;
			map[cls.id] = `rgba(${r}, ${g}, ${b}, ${a})`;
		}
	}

	// Special categories
	if (classificationResults.below_minimum?.count > 0) {
		map['below_minimum'] = 'rgba(128, 128, 128, 0.5)';
	}
	if (classificationResults.unclassified?.count > 0) {
		map['unclassified'] = 'rgba(100, 100, 100, 0.3)';
	}

	return map;
}
